import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import PageContainer from '@/common/components/PageContainer'
import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import { Skeleton } from '@/common/ui/skeleton'
import {
	useQueryScheduleById,
	useMutationUpdateSchedule,
} from '../../api/hooks'
import { transactionSettingsKeys } from '../../api/keys'
import { ScheduleForm } from '../../components'
import type { CreateTransactionScheduleDto, TransactionType } from '../../types'

const EditSchedulePage = () => {
	const navigate = useNavigate()
	const { id } = useParams<{ id: string }>()
	const [searchParams] = useSearchParams()
	const queryClient = useQueryClient()

	const type = (searchParams.get('type') as TransactionType) || 'deposit'
	const typeLabel = type === 'deposit' ? 'ฝากเงิน' : 'ถอนเงิน'
	const backPath = `/operator/transaction-settings/${type}`

	const { data: schedule, isLoading } = useQueryScheduleById(id || '')

	const updateMutation = useMutationUpdateSchedule({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: transactionSettingsKeys.schedules(type),
			})
			queryClient.invalidateQueries({
				queryKey: transactionSettingsKeys.status(type),
			})
			queryClient.invalidateQueries({
				queryKey: transactionSettingsKeys.schedule(id || ''),
			})
			toast.success('แก้ไขตารางเวลาสำเร็จ')
			navigate(backPath)
		},
		onError: () => {
			toast.error('เกิดข้อผิดพลาดในการแก้ไขตารางเวลา')
		},
	})

	const handleSubmit = (data: CreateTransactionScheduleDto) => {
		if (!id) return
		updateMutation.mutate({ id, type, data })
	}

	return (
		<PageContainer>
			<div className="space-y-6 px-4 sm:px-0">
				<Heading
					title={`แก้ไขตารางเวลาปิด${typeLabel}`}
					description="แก้ไขช่วงเวลาที่ระบบจะปิดอัตโนมัติทุกวัน"
				/>

				<Separator />

				<div className="max-w-2xl">
					{isLoading ? (
						<div className="space-y-4">
							<Skeleton className="h-20 w-full" />
							<Skeleton className="h-20 w-full" />
							<Skeleton className="h-16 w-full" />
						</div>
					) : schedule ? (
						<ScheduleForm
							initialData={schedule}
							onSubmit={handleSubmit}
							isLoading={updateMutation.isPending}
							onCancel={() => navigate(backPath)}
						/>
					) : (
						<div className="py-8 text-center text-muted-foreground">
							ไม่พบตารางเวลาที่ต้องการแก้ไข
						</div>
					)}
				</div>
			</div>
		</PageContainer>
	)
}

export default EditSchedulePage
